"use client";
import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";
import { Container } from "@/components/ui/Container";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { safeSessionStorage } from "@/lib/storage/safe-storage";

/**
 * ChunkLoadError = chunk JS référencé par l'HTML mais purgé du serveur après
 * un re-deploy. Classique de Next.js + PM2 reload : l'ancien container sert
 * un HTML, le nouveau a des chunks au hash différent.
 *
 * Détection : message contient "ChunkLoadError", "Loading chunk" ou MIME type
 * "text/plain" (chunk 404 → text/plain HTML retourné). Dans ce cas, on force
 * un reload complet de la page (location.reload) qui re-fetch l'HTML frais
 * et donc les bons hashes de chunks.
 */
function isChunkLoadError(error: Error): boolean {
  const msg = error.message || "";
  const stack = error.stack || "";
  return (
    error.name === "ChunkLoadError" ||
    msg.includes("ChunkLoadError") ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk") ||
    msg.includes("Failed to load chunk") ||
    stack.includes("ChunkLoadError")
  );
}

/**
 * Erreurs reseau transitoires non-actionables (Sentry 2026-05-13).
 *
 * Symptomes : "Load failed" (Safari iOS), "Failed to fetch" (Chrome/Firefox),
 * "NetworkError", "AbortError", "The user aborted a request". Causes :
 * - User backgrounde le tab pendant un fetch
 * - Switch wifi/cellulaire en cours de requete
 * - Navigation vers une autre page avant fin du fetch
 * - Connexion lente (timeout side-effect)
 *
 * Ne sont PAS des bugs notre code, juste du bruit Sentry. Skip report
 * pour ne pas saturer le dashboard ni polluer les alertes.
 */
function isTransientNetworkError(error: Error): boolean {
  const msg = (error.message || "").toLowerCase();
  const name = (error.name || "").toLowerCase();
  return (
    name === "aborterror" ||
    msg === "load failed" || // Safari iOS classique
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("network error") ||
    msg.includes("the user aborted") ||
    msg.includes("the operation was aborted") ||
    msg.includes("network request failed") ||
    msg.includes("net::err_")
  );
}

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Auto-recovery sur ChunkLoadError → reload une seule fois (pas de boucle infinie)
    if (isChunkLoadError(error)) {
      const KEY = "sahha:chunk-reload";
      // safeSessionStorage obligatoire ici : c'est la frontière d'erreur
      // globale. Un throw sur le stockage y remplacerait l'erreur d'origine
      // par la sienne, et la page censée rattraper le crash crasherait.
      const last = safeSessionStorage.getItem(KEY);
      const now = Date.now();
      if (!last || now - parseInt(last, 10) > 30_000) {
        safeSessionStorage.setItem(KEY, String(now));
        window.location.reload();
        return;
      }
    }

    // Skip Sentry reporting pour les erreurs reseau transitoires non-actionnables.
    // Cf bug 2026-05-13 : Safari iOS "Load failed" spammait /api/errors/client.
    if (isTransientNetworkError(error)) return;

    Sentry.captureException(error);

    if (process.env.NODE_ENV === "production") {
      void fetch("/api/errors/client", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          message: error.message,
          digest: error.digest,
          stack: error.stack?.slice(0, 4000),
          url: window.location.href,
        }),
        keepalive: true,
      }).catch(() => {});
    }
  }, [error]);

  return (
    <Container className="py-20 max-w-xl">
      <Alert
        variant="danger"
        title="Une erreur est survenue"
      >
        Nous rencontrons un problème technique. Réessayez dans quelques instants
        ou retournez à l&apos;accueil.
      </Alert>
      {error.digest ? (
        <p className="mt-4 text-xs text-[var(--sahha-text-500)]">
          Référence&nbsp;: <code>{error.digest}</code>
        </p>
      ) : null}
      <div className="mt-6 flex gap-3 flex-wrap">
        <Button onClick={reset}>Réessayer</Button>
        <Button variant="outline" onClick={() => (window.location.href = "/")}>
          Retour à l&apos;accueil
        </Button>
      </div>
    </Container>
  );
}
